import {useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import AtomImage from '@s-ui/react-atom-image'
import {useSuiContext} from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import PrimitiveTypography, {primitiveTypographyDesign} from '@s-ui/react-primitive-typography'
import {useLocation, useRouter} from '@s-ui/react-router'
import EmptyCaseDefault, {emptyCaseDefaultSizes} from '@adv-mt/mt-empty-case-default'
import {useNotificationManager} from '@adv-mt/mt-hook-use-notification-manager'
import Layout from '@adv-mt/mt-layout-basic'
import ModalBasic, {moleculeModalSizes} from '@adv-mt/mt-modal-basic'
import NavigationRequireAuth from '@adv-mt/mt-navigation-require-auth'
import TitleUserArea from '@adv-mt/mt-title-user-area'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../../components/LinkFactory/index.js'
import WrapperAlerts from './wrapper/index.js'
import {BASE_CLASS} from './config.js'
import getInitialProps from './getInitialProps.js'

const MODAL_CLASS = `${BASE_CLASS}-modal`
const MAX_LIMIT_REACHED_END_DATE = new Date('2026-04-27')
const TODAY = new Date()
const MAX_LIMIT_REACHED_MODAL_SEEN_KEY = 'max-limit-reached-modal-seen-key'

const UserAlerts = ({helmet, listFiltersOptions, pageName, pageTrackingData, userAlerts, userAlertsError}) => {
  const {i18n, domain} = useSuiContext()
  const {push} = useRouter()
  const {pathname} = useLocation()
  const {page: analyticsTrackPage} = useAnalyticsTrack()

  const config = domain.get('config')
  const URL_STATICS_IMAGES = config.get('URL_STATICS_IMAGES')
  const imageUrl = `${URL_STATICS_IMAGES}pictograms/PackReference.svg`

  const {
    SEARCH_SAVE: {MAX_ALERTS_LIMIT_REACHED}
  } = config.get('COMMON')

  const formattedEndDate = MAX_LIMIT_REACHED_END_DATE.toLocaleDateString(i18n.culture, {
    day: 'numeric',
    month: 'long'
  })

  const {showNotification, setIsViewed} = useNotificationManager({
    key: MAX_LIMIT_REACHED_MODAL_SEEN_KEY
  })

  const [isModalVisible, setIsModalVisible] = useState(false)

  useMount(() => analyticsTrackPage(pageTrackingData))

  useEffect(() => {
    const hasReachedMaxLimit = userAlerts.length > MAX_ALERTS_LIMIT_REACHED
    const showMaxLimitModal = showNotification && hasReachedMaxLimit && MAX_LIMIT_REACHED_END_DATE >= TODAY

    setIsModalVisible(showMaxLimitModal)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNotification])

  const handleUpdateAlert = () => {
    push(pathname)
  }

  const onCloseModal = () => {
    setIsModalVisible(false)
    setIsViewed(true)
  }

  const buttonPrimary = (
    <AtomButton onClick={onCloseModal} shape={atomButtonShapes.CIRCULAR} size={atomButtonSizes.LARGE} fullWidth>
      {i18n.t('COMMON.UNDERSTOOD')}
    </AtomButton>
  )

  return (
    <NavigationRequireAuth currentPath={pathname}>
      <Layout.Basic linkFactory={LinkFactory}>
        <Helmet {...helmet} />
        <main className={BASE_CLASS}>
          <section className={`${BASE_CLASS}-content`}>
            <TitleUserArea title={i18n.t('NAVIGATION_USER_COCHES_ALERTS_TEXT')} />
            {userAlertsError ? (
              <EmptyCaseDefault
                imageUrl="images/empty-case-search.svg"
                imageSize={emptyCaseDefaultSizes.MEDIUM}
                text={i18n.t('NOTIFICATIONS.NETWORK_ERROR')}
              />
            ) : (
              <WrapperAlerts
                currentAlerts={userAlerts}
                listFiltersOptions={listFiltersOptions}
                onUpdateAlert={handleUpdateAlert}
                pageName={pageName}
              />
            )}
          </section>

          <ModalBasic
            isOpen={isModalVisible}
            onClose={onCloseModal}
            size={moleculeModalSizes.SMALL}
            buttonPrimary={buttonPrimary}
          >
            <div className={MODAL_CLASS}>
              <AtomImage src={imageUrl} className={`${MODAL_CLASS}-image`} />
              <PrimitiveTypography design={primitiveTypographyDesign.HEADLINE_1} className={`${MODAL_CLASS}-title`}>
                {i18n.t('ALERTS_MAX_LIMIT_REACHED_MODAL_TITLE')}
              </PrimitiveTypography>
              <PrimitiveTypography design={primitiveTypographyDesign.BODY_1}>
                {i18n.t('ALERTS_MAX_LIMIT_REACHED_MODAL_TEXT', {
                  maxDate: formattedEndDate,
                  maxAlerts: MAX_ALERTS_LIMIT_REACHED
                })}
              </PrimitiveTypography>
            </div>
          </ModalBasic>
        </main>
      </Layout.Basic>
    </NavigationRequireAuth>
  )
}

UserAlerts.displayName = 'UserAlerts'
UserAlerts.propTypes = {
  helmet: PropTypes.object,
  listFiltersOptions: PropTypes.object,
  pageName: PropTypes.string,
  pageTrackingData: PropTypes.object,
  userAlerts: PropTypes.array,
  userAlertsError: PropTypes.object
}
UserAlerts.getInitialProps = getInitialProps

export default UserAlerts
